import React, { useState, useEffect, useCallback, useRef } from "react";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import ReportPageAction from "../page-action/ReportPageAction";
import ErrorBoundary from "../../../components/error-boundary";
import ComponentViewFallback from "../../../components/error-boundary/fallback/ComponentViewFallback";
import AccountsReceivableTable from "../../../components/table/all-tables/reportable/accountsReceivable/AccountsReceivableTable";
import ReportSummaryTotals from "../../../components/reports/ReportSummaryTotals";
import AccountsReceivableFilter from "./AccountsReceivableFilter";
import {
  showToast,
  downloadFileFromResponse,
  formatCalendarDate
} from "../../../utils/utils";
import privateAxios from "../../../utils/axiosPrivate";
import { useColumnSort } from "../../../hooks/useColumnSort";
import {
  getAccountsReceivableReportUrl,
  getAccountsReceivableReportDownloadUrl
} from "../../../utils/constants/apiEndPoints";
import { accountsReceivableData } from "../../../locales/en";
import { statusOptions } from "./statusOptions";
import {
  PAGE_SIZE_50,
  showToastType,
  YYYYMMDD,
  DD_MMM_YYYY,
  HTTP_STATUS_SUCCESS,
  ZERO_NUMBER,
  ENTER_KEY
} from "../../../utils/constants/appConstants";

const { title, summary: summaryLabels } = accountsReceivableData;

const EMPTY_SUMMARY = {
  invoiceCount: ZERO_NUMBER,
  totalPrincipal: ZERO_NUMBER,
  totalFee: ZERO_NUMBER,
  totalPrincipalAndFee: ZERO_NUMBER,
  totalOverdueFee: ZERO_NUMBER,
  totalOverduePaFee: ZERO_NUMBER,
  totalManualPartPayments: ZERO_NUMBER,
  totalPaymentsReceived: ZERO_NUMBER,
  totalBalanceOwing: ZERO_NUMBER
};

const sortInitialState = {
  clientName: null,
  quoteId: null,
  invoiceNumber: null,
  fundedDate: null,
  repayDate: null,
  quoteStatus: null,
  principal: null,
  fee: null,
  overdueFee: null,
  overduePaFee: null,
  manualPartPayments: null,
  paymentsReceived: null,
  balanceOwing: null
};

/**
 * The Accounts Receivable report: every dollar owed to FundTap, or overpaid by a customer,
 * on one chosen date. It lists every invoice still open on the date, and every completed
 * invoice whose balance on the date is not zero. The date is the whole point of the report, so the totals strip repeats
 * it above the table.
 */
export default function AccountsReceivableReport() {
  // The number of the newest report request. Only that request may write its answer.
  const latestRequestRef = useRef(ZERO_NUMBER);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(ZERO_NUMBER);
  const [pageIndex, setPageIndex] = useState(ZERO_NUMBER);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_50);

  const [appliedAsAtDate, setAppliedAsAtDate] = useState(new Date());
  // The date the totals on screen belong to. It moves only when an answer arrives, so
  // the heading never labels the previous totals with the date now being asked for.
  const [summaryAsAtDate, setSummaryAsAtDate] = useState(new Date());
  // True when the last report request failed. The page then shows nothing rather
  // than a position of $0, which a failed request must never be mistaken for.
  const [unavailable, setUnavailable] = useState(false);
  // Counts the times Filter was pressed. React skips a state write that changes
  // nothing, so pressing Filter with the same date and keyword would not start a
  // request, and a report left unavailable by a brief outage could not be retried.
  const [filterPresses, setFilterPresses] = useState(ZERO_NUMBER);
  const [selectedAsAtDate, setSelectedAsAtDate] = useState(new Date());

  const [searchKeyword, setSearchKeyword] = useState("");
  const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");

  // The status filter starts with every status ticked, so the report shows the whole book
  // until the user unticks one. It only takes effect when Filter is pressed, the same as
  // the date and the keyword.
  const [selectedStatuses, setSelectedStatuses] = useState(statusOptions);
  const [appliedStatuses, setAppliedStatuses] = useState(statusOptions);

  const { columnOrders, handleSort } = useColumnSort(sortInitialState);

  const activeSortColumn = Object.keys(columnOrders).find(
    (key) => columnOrders[key]
  );
  const sortBy = activeSortColumn || undefined;
  const sortOrder = activeSortColumn
    ? columnOrders[activeSortColumn]
    : undefined;

  const handleColumnSort = (columnId) => {
    handleSort(columnId);
    setPageIndex(ZERO_NUMBER);
  };

  const buildParams = useCallback(() => {
    const params = {
      // The date the user picked, sent as the calendar day they see. Converting it to
      // another timezone first would run the report for the day before or after.
      asAtDate: appliedAsAtDate
        ? formatCalendarDate(appliedAsAtDate, YYYYMMDD)
        : undefined
    };
    if (appliedSearchKeyword) {
      params.search = appliedSearchKeyword;
    }
    // The filter sends the quote status ids of the ticked statuses, which the backend puts
    // straight into its database query. No ticked status means no parameter, and the backend
    // then reports every reportable status. Comma-joined, not an array: the backend binds
    // one comma-separated value to its status-id list, which avoids the bracketed array keys
    // the default serializer would send.
    if (appliedStatuses.length > ZERO_NUMBER) {
      params.statusIds = appliedStatuses.map((status) => status.id).join(",");
    }
    if (sortBy) {
      params.sortBy = sortBy;
      params.sortOrder = sortOrder;
    }
    return params;
  }, [
    appliedAsAtDate,
    appliedSearchKeyword,
    appliedStatuses,
    sortBy,
    sortOrder
  ]);

  const showRequestError = (error) => {
    if (error?.response?.data?.errors?.length > ZERO_NUMBER) {
      showToast(error.response.data.errors[0], showToastType.error);
    } else {
      showToast(error?.response?.data?.message, showToastType.error);
    }
  };

  /**
   * A failed report is shown as unavailable, never as a position of $0.
   *
   * <p>The report fails on purpose rather than answering an outage with a number,
   * so the page must not undo that by rendering zero rows and zero totals under
   * the date that was asked for. A toast disappears; the figures do not. The rows
   * and the totals are cleared, and the page says the position could not be read.
   */
  const markUnavailable = () => {
    setData([]);
    setSummary(EMPTY_SUMMARY);
    setTotalPages(ZERO_NUMBER);
    setUnavailable(true);
  };

  const showDownloadError = async (error) => {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        showToast(
          parsed?.errors?.[ZERO_NUMBER] || parsed?.message,
          showToastType.error
        );
        return;
      } catch {
        // Not JSON. Fall through to the shared reader, which shows a generic failure.
      }
    }
    showRequestError(error);
  };

  const fetchData = useCallback(async () => {
    // Sorting or paging starts a new request before the last one answers, and the
    // report takes long enough for them to finish out of order. Each request takes a
    // number and only the newest one is allowed to write, so a slow earlier answer
    // cannot replace the rows and totals of the selection now on screen.
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    const isStale = () => requestId !== latestRequestRef.current;

    setLoading(true);
    try {
      const params = { ...buildParams(), page: pageIndex, size: pageSize };
      const response = await privateAxios.get(getAccountsReceivableReportUrl, {
        params
      });
      if (isStale()) {
        return;
      }
      if (response.data && response.data.status === HTTP_STATUS_SUCCESS) {
        setData(response.data.data?.rows || []);
        setSummary(response.data.data?.summary || EMPTY_SUMMARY);
        setTotalPages(response.data.pageInfo?.totalPages || ZERO_NUMBER);
        setSummaryAsAtDate(appliedAsAtDate);
        setUnavailable(false);
      } else {
        markUnavailable();
      }
    } catch (error) {
      if (isStale()) {
        return;
      }
      showRequestError(error);
      markUnavailable();
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [appliedAsAtDate, buildParams, filterPresses, pageIndex, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = () => {
    setAppliedAsAtDate(selectedAsAtDate);
    setAppliedSearchKeyword(searchKeyword);
    setAppliedStatuses(selectedStatuses);
    setPageIndex(ZERO_NUMBER);
    // Always changes, so Filter asks again even when nothing else moved.
    setFilterPresses((pressed) => pressed + 1);
  };

  const handleStatusChange = (selected) => {
    setSelectedStatuses(selected || []);
  };

  const handleKeyDown = (e) => {
    if (e.key === ENTER_KEY) {
      handleFilter();
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchKeyword(value);
    if (value === "") {
      setAppliedSearchKeyword("");
      setPageIndex(ZERO_NUMBER);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const response = await privateAxios.get(
        getAccountsReceivableReportDownloadUrl,
        { params: buildParams(), responseType: "blob" }
      );
      downloadFileFromResponse(response);
    } catch (error) {
      // The request asked for a blob, so a failure arrives as a Blob too and the
      // usual error reader finds nothing on it. Read the text back into JSON first,
      // otherwise a refusal that says how to recover — narrow the search — reaches
      // the user as a bare failure.
      await showDownloadError(error);
    } finally {
      setLoading(false);
    }
  };

  const summaryItems = [
    {
      label: summaryLabels.invoices,
      value: summary.invoiceCount,
      isCurrency: false
    },
    { label: summaryLabels.principal, value: summary.totalPrincipal },
    { label: summaryLabels.fee, value: summary.totalFee },
    {
      label: summaryLabels.principalAndFee,
      value: summary.totalPrincipalAndFee
    },
    { label: summaryLabels.overdueFee, value: summary.totalOverdueFee },
    {
      label: summaryLabels.overduePaFee,
      value: summary.totalOverduePaFee
    },
    {
      label: summaryLabels.manualPartPayments,
      value: summary.totalManualPartPayments
    },
    {
      label: summaryLabels.paymentsReceived,
      value: summary.totalPaymentsReceived
    },
    {
      label: summaryLabels.balanceOwing,
      value: summary.totalBalanceOwing,
      isTotal: true
    }
  ];

  return (
    <>
      <TablePagesTitle mainTitle={title} />
      <ReportPageAction subheading={title} />

      <div className="row mb-2 mx-0">
        <div className="col-12 px-0">
          <AccountsReceivableFilter
            selectedAsAtDate={selectedAsAtDate}
            handleAsAtDateChange={setSelectedAsAtDate}
            searchKeyword={searchKeyword}
            handleSearchChange={handleSearchChange}
            handleKeyDown={handleKeyDown}
            selectedStatuses={selectedStatuses}
            handleStatusChange={handleStatusChange}
            onFilter={handleFilter}
            onDownload={handleDownload}
            loading={loading}
          />
        </div>
      </div>

      <div className="row mb-2 mx-0">
        <div className="col-12 px-0">
          {unavailable ? (
            <div className="alert alert-warning mb-0" role="alert">
              {summaryLabels.unavailable}
            </div>
          ) : (
            <ReportSummaryTotals
              heading={`${summaryLabels.heading} ${formatCalendarDate(summaryAsAtDate, DD_MMM_YYYY)}`}
              items={summaryItems}
            />
          )}
        </div>
      </div>

      <ErrorBoundary fallback={<ComponentViewFallback />}>
        <div className="row mt-4 mb-2 mx-0">
          <div className="col-md-12 px-0">
            <AccountsReceivableTable
              data={data}
              loading={loading}
              totalPages={totalPages}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageIndex={setPageIndex}
              setPageSize={setPageSize}
              columnOrders={columnOrders}
              handleSort={handleColumnSort}
            />
          </div>
        </div>
      </ErrorBoundary>
    </>
  );
}
